import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useSellingDisabled } from '../hooks/useSellingDisabled';
import { useContact } from '../contexts/ContactContext';

const OUTSTANDING_STATUSES = ['sent', 'overdue'];

const formatCents = (cents) => {
  const n = (cents || 0) / 100;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

/**
 * Renders a warning banner when the current user has been suspended from
 * selling due to outstanding recovery invoices.
 *
 * Renders nothing when UserContext is loading OR the user is not suspended.
 *
 * On mount (only when disabled === true), runs ONE Firestore query against
 * platformBilling for the current user's most recent outstanding invoice
 * to populate the "Pay" CTA. Uses the existing (userId ASC, createdAt DESC)
 * composite index — no new index required.
 *
 * Modes:
 *   - "full" — large bordered card (Profile, AddPedal pages)
 *   - "inline" — compact one-line variant (offer-accept screens)
 */
const SellingDisabledBanner = ({ mode = 'full' }) => {
  const { loading, disabled, outstandingCount, outstandingAmountCents } = useSellingDisabled();
  const { openContact } = useContact();
  const [hostedInvoiceUrl, setHostedInvoiceUrl] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (loading || !disabled) return;
    const user = auth.currentUser;
    if (!user) return;

    let cancelled = false;
    setFetching(true);
    (async () => {
      try {
        const q = query(
          collection(db, 'platformBilling'),
          where('userId', '==', user.uid),
          where('status', 'in', OUTSTANDING_STATUSES),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        if (!snap.empty) {
          const data = snap.docs[0].data();
          setHostedInvoiceUrl(data.hostedInvoiceUrl || null);
        }
      } catch (err) {
        // Best-effort. Banner still renders without the CTA URL.
        console.warn('SellingDisabledBanner: failed to fetch hosted invoice URL', err);
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => { cancelled = true; };
  }, [loading, disabled]);

  if (loading || !disabled) return null;

  const amountStr = formatCents(outstandingAmountCents);
  const countStr = outstandingCount === 1 ? '1 invoice' : `${outstandingCount} invoices`;

  // ---- Inline (compact) variant ---------------------------------------
  if (mode === 'inline') {
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '10px 14px',
          margin: '12px 0',
          backgroundColor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          fontSize: '0.9rem',
          color: '#78350F',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          ⚠ Selling, trading & listing are paused on your account.
        </span>
        {hostedInvoiceUrl ? (
          <a
            href={hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '6px 12px',
              backgroundColor: '#C9A227',
              color: '#fff',
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            Pay outstanding invoice
          </a>
        ) : fetching ? (
          <span style={{ fontStyle: 'italic' }}>Loading invoice link…</span>
        ) : (
          <span>Check your email for the invoice from PedalxChange.</span>
        )}
      </div>
    );
  }

  // ---- Full (large bordered) variant ----------------------------------
  return (
    <div
      role="alert"
      style={{
        padding: '20px 24px',
        margin: '16px 0',
        backgroundColor: '#FEF3C7',
        border: '2px solid #D97706',
        borderRadius: '12px',
        color: '#78350F',
      }}
    >
      <div style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '8px' }}>
        ⚠ Selling, trading & listing are paused on your account
      </div>
      <div style={{ fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '14px' }}>
        You have <strong>{amountStr}</strong> in outstanding recovery invoices ({countStr}).
        Selling pedals, creating new listings, accepting purchase or trade offers, and
        reactivating listings are paused until your balance is cleared. Buying, browsing,
        and managing your Wantlist are unaffected.
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        {hostedInvoiceUrl ? (
          <a
            href={hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '10px 18px',
              backgroundColor: '#C9A227',
              color: '#fff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            Pay outstanding invoice
          </a>
        ) : fetching ? (
          <span style={{ fontStyle: 'italic', color: '#92400E' }}>
            Loading invoice link…
          </span>
        ) : (
          <span style={{ color: '#92400E' }}>
            Check your email for the invoice from PedalxChange.
          </span>
        )}
        <Link
          to="/policies"
          style={{ color: '#78350F', textDecoration: 'underline', fontSize: '0.9rem' }}
        >
          View Terms §3a
        </Link>
        <button
          type="button"
          onClick={() => openContact({
            subject: 'Question about my outstanding invoice',
          })}
          style={{
            background: 'none',
            border: 'none',
            color: '#78350F',
            textDecoration: 'underline',
            fontSize: '0.9rem',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Contact support
        </button>
      </div>
    </div>
  );
};

export default SellingDisabledBanner;
